import React, {useCallback, useEffect, useLayoutEffect, useRef, useState} from 'react';
import {useNavigate} from "react-router-dom";
import EmptyState from "../../../components/EmptyState";
import {Skeleton} from "antd-mobile";
import useHistoryControl from "../../../hooks/useHistoryControl";
import PickUpLayout, {usePickUpLayout} from "../layouts/PickUpLayout";
import PickUpSearchBar from "./components/PickUpSearchBar";
import PickingUpCard from "./components/PickingUpCard";
import {usePickedUpFailedRetry, UsePickedUpFailedRetryResult} from "./hooks/usePickedUpFailedRetry";
import PickUpUploadNotification from "./components/PickUpUploadNotification";
import {t} from "../../../i18n";
import { usePickupTaskAppointmentList, useRefreshPickupTaskAppointmentList, useWorkPreviewMode } from '../store/pickupStore';
import {PickupAppointmentType} from "../graphql/types/PickupTaskWithAppointmentsType";
import {client} from "../../../graphql/client";
import PickupStartWorkPage from "../startWork/PickupStartWorkPage";
import PickUpWorkPreviewBanner from "../components/PickUpWorkPreviewBanner";
import {Toast} from "antd-mobile";
import {startPickupWorkNonBlocking} from "../graphql/mutations/startPickupWork";
import {flushPendingWorkStarts, hasPendingWorkStart, savePendingWorkStart} from "../services/PickupWorkStartRetry";
import {PickupFinishedPath} from "../../../enums/PickUpNavigatePathEnum";
import {
    GET_PICKUP_WORK_START_INFO,
    GetPickupWorkStartInfoResponse,
    PickupWorkStartInfo
} from "../graphql/queries/getPickupWorkStartInfo";

interface BodyProps {
    retry: UsePickedUpFailedRetryResult;
    /** 「仅查看今日任务」只读态：卡片不可点，司机只能看 */
    readOnly: boolean;
}

const Body: React.FC<BodyProps> = ({retry, readOnly}) => {
    const [searchValue, setSearchValue] = useState<string>('');
    const [uploadStatus, setUploadStatus] = useState<'retrying' | 'success' | 'none'>('none');
    const [pickingUpDataList, setPickingUpDataList] = useState<PickupAppointmentType[]>([]);
    const {failedUploadCount, isRetrying, showRetryingHint, handleRetry} = retry;
    const {refreshFailedCount} = usePickUpLayout();
    const { pickupTaskAppointmentList: pickingUpData, loading, refreshPickupTaskAppointmentList } = usePickupTaskAppointmentList();
    const mainContainerRef = useRef<HTMLDivElement>(null);

    useHistoryControl("/pickup/picking-up", {
        useReplace: true,
        controlNavButton: false
    });

    useEffect(() => {
        // 只有在重试数据初始化完成后才执行校验逻辑
        const timer = setTimeout(() => {
            if (failedUploadCount <= 0 && !isRetrying){
                refreshPickupTaskAppointmentList();
            }
        }, 100);

        // 清理定时器，防止内存泄漏
        return () => clearTimeout(timer);
    }, [failedUploadCount, isRetrying, refreshPickupTaskAppointmentList]);

    useEffect(() => {
        if (pickingUpData.length > 0){
            const filteredData = pickingUpData.filter(appointment => !appointment.isFinished);
            setPickingUpDataList(filteredData); // 使用修改后的数据
        }
    }, [pickingUpData]);

    useEffect(() => {
        if (showRetryingHint) {
            setUploadStatus('retrying');
        } else if (uploadStatus === 'retrying') {
            setUploadStatus('success');
            refreshFailedCount();
        }
    }, [showRetryingHint, uploadStatus, refreshFailedCount]);

    const triggerRetry = useCallback(() => {
        if (failedUploadCount > 0 && !isRetrying) {
            handleRetry();
        }
    }, [failedUploadCount, isRetrying, handleRetry]);

    const handleNotificationClick = useCallback(() => {
        if (uploadStatus === 'success') {
            setUploadStatus('none');
        } else if (uploadStatus === 'none' && failedUploadCount > 0) {
            triggerRetry();
        }
    }, [uploadStatus, failedUploadCount, triggerRetry]);

    const handleSearch = async (value: string) => {
        setSearchValue(value);
    };

    const filteredPickingUp = pickingUpDataList.filter(appointment =>
        appointment?.channelName?.toLowerCase().includes(searchValue.toLowerCase()) ?? false
    );

    // 恢复主容器滚动位置
    useLayoutEffect(() => {
        const el = document.getElementById('pickup-main-container');
        const y = Number(sessionStorage.getItem('pickupPageScrollTop') || '0');
        if (el && y > 0) {
            el.scrollTo({ top: y, behavior: 'auto' });
            sessionStorage.removeItem('pickupPageScrollTop'); // 恢复一次后清空
        }
    }, []);

    return (
        <div className="flex flex-col h-screen bg-gray-50 gap-2 p-2" ref={mainContainerRef}>
            <PickUpUploadNotification
                status={uploadStatus}
                onClick={handleNotificationClick}
            />

            <PickUpSearchBar onSearch={handleSearch}/>

            <div className="flex-1">
                {loading ? (
                    <div className="flex flex-col h-screen">
                        <Skeleton.Title animated/>
                        <Skeleton.Paragraph lineCount={3} animated/>
                        <Skeleton.Title animated/>
                        <Skeleton.Paragraph lineCount={3} animated/>
                        <Skeleton.Title animated/>
                        <Skeleton.Paragraph lineCount={3} animated/>
                        <Skeleton.Title animated/>
                        <Skeleton.Paragraph lineCount={3} animated/>
                        <Skeleton.Title animated/>
                        <Skeleton.Paragraph lineCount={3} animated/>
                    </div>
                ) : (
                    <>
                        {filteredPickingUp.length > 0 ? (
                            <div>
                                {filteredPickingUp.map((pickingUp, index) => (
                                    <PickingUpCard
                                        key={index}
                                        data={pickingUp}
                                        readOnly={readOnly}
                                    />
                                ))}
                            </div>
                        ) : (
                            <EmptyState description={t(`pages.pickUp.noPickUpInfo`)}/>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};


/**
 * 「**今日无任务**」降级态下已滑过「滑动查看历史作业」的标记。带日期是因为 App 常整夜不关，
 * 跨天后必须重新触发一次；用 sessionStorage 而非 localStorage：每次重新打开 App 都该按
 * PRD「进入系统触发」再走一遍。
 *
 * **只对无任务降级态生效**，绝不能用来挡「有待开始任务」的滑块——滑块是**任务维度**的
 * （prd-clarify 1.2 已确认），由后端的 pendingTaskId 判定，本标记无权干预。
 */
const workStartDismissKey = () => `pickup_work_start_dismissed_${new Date().toDateString()}`;

// 组件名必须大写开头：react-hooks/rules-of-hooks 靠首字母判定「这是不是组件」，
// 小写会被当成普通函数，里面调 useXxx 一律报错。本组件原先不含 hook 所以一直没暴露，
// 开始揽收加了 useState/useEffect 后 build 直接失败。
const PickingUpPage: React.FC = () => {
    // 重传 hook 放在启动页判断之外：它内部用 ref 防重入，放进 Body 会让「停在启动页不滑动」
    // 的司机（尤其今日无任务、只是打开看一眼）离线队列一直不上传
    const retry = usePickedUpFailedRetry();
    const navigate = useNavigate();
    // 只拿刷新函数，不订阅列表也不启轮询——启动页判断「是否真没活干」用，见 handleViewHistory
    const refreshPickupTaskAppointmentList = useRefreshPickupTaskAppointmentList();
    // 只读态放 store：地图是独立路由，也要跟着只读
    const {workPreviewMode, setWorkPreviewMode} = useWorkPreviewMode();
    const [workStartInfo, setWorkStartInfo] = useState<PickupWorkStartInfo | null>(null);
    const [checkingWorkStart, setCheckingWorkStart] = useState<boolean>(true);
    const [startingWork, setStartingWork] = useState<boolean>(false);
    const [workStartDismissed, setWorkStartDismissed] = useState<boolean>(
        () => sessionStorage.getItem(workStartDismissKey()) === '1'
    );
    // 本地是否还存着没传上去的打点。有 = 司机已经点过开始作业，滑块不该再拦他
    const [pendingWorkStart, setPendingWorkStart] = useState<boolean>(false);

    // 不能因为 dismissed 就跳过查询：滑块是任务维度的，只有这个查询能拿到 pendingTaskId。
    // 早先在这里提前 return，导致司机「今日无任务时滑过查看历史」之后，调度当天再排的任务
    // 不会再出滑块，act_start_work_time 永远写不进去——恰恰漏掉了最需要打点的临时加单场景。
    useEffect(() => {
        let cancelled = false;

        // 失败先退避重试再放行。这个查询是「开始作业」打点的**唯一触发源**——
        // 拿不到 pendingTaskId 就不出启动页、不出横幅，司机会绕过两个写入口直接干活，
        // 该任务的 act_departure_time 永久为空，且页面上毫无提示（静默丢数据）。
        // 主要成因是瞬时抖动，几百毫秒后重试即可恢复；仍失败才放行——
        // 打点不是业务前置条件，长时间断网时拦住司机等于让他整天开不了工。
        const fetchWorkStartInfo = async (): Promise<boolean> => {
            const maxAttempts = 3;
            for (let attempt = 1; attempt <= maxAttempts; attempt++) {
                try {
                    const res = await client.query<GetPickupWorkStartInfoResponse>({
                        query: GET_PICKUP_WORK_START_INFO,
                        fetchPolicy: 'network-only',
                    });
                    if (!cancelled) {
                        setWorkStartInfo(res.data?.getPickupWorkStartInfo ?? null);
                    }
                    return true;
                } catch (error) {
                    if (cancelled) {
                        return false;
                    }
                    if (attempt === maxAttempts) {
                        console.error('getPickupWorkStartInfo failed after retries (non-blocking):', error);
                        return false;
                    }
                    await new Promise((resolve) => setTimeout(resolve, 600 * attempt));
                }
            }
            return false;
        };

        // 先读本地待补打点，再查后端。顺序不能反：断网时查询必然失败，
        // 若此时 pendingWorkStart 仍是初值 false，滑块会把已经点过开始作业的司机再拦一次
        const run = async () => {
            const pending = await hasPendingWorkStart();
            if (cancelled) {
                return;
            }
            setPendingWorkStart(pending);

            const online = await fetchWorkStartInfo();
            if (cancelled || !online || !pending) {
                return;
            }

            // 查询成功本身就是「有网」的信号，借它补传，不另起轮询或网络监听
            const {compensatedTaskIds, remaining} = await flushPendingWorkStarts();
            if (cancelled) {
                return;
            }
            // remaining=false 且没补成功，说明记录被后端拒绝或已过保留期而丢弃——
            // 此时必须放开滑块提示，否则这个任务的打点入口就彻底没了
            setPendingWorkStart(remaining);
            if (compensatedTaskIds.length > 0) {
                setWorkStartInfo((prev) => (
                    prev && prev.pendingTaskId && compensatedTaskIds.includes(prev.pendingTaskId)
                        ? {...prev, pendingTaskId: null}
                        : prev
                ));
            }
        };

        run().finally(() => {
            if (!cancelled) {
                setCheckingWorkStart(false);
            }
        });
        return () => {
            cancelled = true;
        };
        // 只在进入页面时查一次：依赖 workStartDismissed 会让「滑动查看历史」后又多发一次无用请求
    }, []);

    // 启动页滑动那一刻重新核一次。启动信息是进页面查的一次快照，而「本日任务暂未安排」的司机
    // 会长时间停在启动页等派单——期间调度排了任务，按旧快照放行就跳过了打点（见 PickupStartWorkPage）。
    // 失败返回 null，由调用方按「放行」处理：拦住司机比漏一次打点更糟
    const refreshWorkStartInfo = async (): Promise<PickupWorkStartInfo | null> => {
        try {
            const res = await client.query<GetPickupWorkStartInfoResponse>({
                query: GET_PICKUP_WORK_START_INFO,
                fetchPolicy: 'network-only',
            });
            const fresh = res.data?.getPickupWorkStartInfo ?? null;
            setWorkStartInfo(fresh);
            return fresh;
        } catch (error) {
            console.error('refresh getPickupWorkStartInfo failed (non-blocking):', error);
            return null;
        }
    };

    // 离开启动页的**唯一出口**，滑块与只读态横幅两条路径共用。
    // 必须清掉 pendingTaskId：needStartPage 是以它为准的，只设 dismissed 挡不住——
    // 司机滑完会打点成功却仍停在启动页，看上去「滑了没反应」于是反复滑。
    // （今日无任务的降级态下它本来就是 null，清它无副作用）
    const handleWorkStartDone = () => {
        sessionStorage.setItem(workStartDismissKey(), '1');
        setWorkStartDismissed(true);
        setWorkPreviewMode(false);
        setWorkStartInfo((prev) => (prev ? {...prev, pendingTaskId: null} : prev));
    };

    // 「滑动查看历史作业」：今日无任务时的降级出口。滑完直接进「已完成」页——
    // 原先落回揽收中列表，司机看到的是一个空列表，还得自己去菜单里翻「已完成」，
    // 与滑块上写的文案对不上。
    //
    // **但「今日无任务」不等于「司机没活干」**：启动页的 hasTodayTask 只看 task_date=今天，
    // 而揽收中列表走的 getPublishedTaskByDriver **根本不按日期过滤**，只看
    // status IN (PUBLISHED, IN_PROGRESS)。昨天没跑完的任务在前者查不到、在后者查得到——
    // 直接跳走会把还有活要收尾的司机送离作业列表，而他刚被告知「今日无任务」，
    // 没有任何理由去按返回键。故跳转前先拉一次列表确认。
    //
    // 这次列表查询自身失败时 store 返回旧缓存（启动页阶段是空数组），仍会跳走；
    // 但真正的弱网已由 PickupStartWorkPage 那层的 onRefresh 拦掉（拿不到确定答案就不跳），
    // 能走到这里说明几百毫秒前网络还是通的，残留概率可接受。
    const handleViewHistory = async () => {
        const stillWorking = await refreshPickupTaskAppointmentList();
        // dismissed 必须置：已完成页的返回指回揽收中，不置会被启动页再拦一次，
        // 形成「滑 → 已完成 → 返回 → 又要滑」的循环
        handleWorkStartDone();
        if (stillWorking.length > 0) {
            // 还有在跑的任务，留在揽收中列表，与本次改造前一致
            return;
        }
        // 不传 selectedDate，沿用已完成页自己的日期口径。
        // 曾想按「昨日回顾」把默认日期设成昨天，但那个统计是后端 LocalDate.now() 按 **JVM 时区** 算的，
        // 前端 dayjs() 按 **设备时区** 算，两者跨时区会差一天——跳过去反而是空白，
        // 比不设默认值更让人困惑。时区对齐是单独一件事，不在本次范围内
        navigate(PickupFinishedPath.PICKUP_FINISHED);
    };

    // 「仅查看今日任务」：不打点，直接以只读态进列表。
    // 司机想先看今天的任务量和路线再决定什么时候开工——看任务不该被记成已开工，
    // 否则 act_start_work_time 失真、需求点 1「监控司机实际开始作业时间」的目的落空
    const handlePreviewTasks = () => {
        setWorkPreviewMode(true);
    };

    // 只读态横幅的「开始作业」：与启动页滑块共用同一个打点入口（startPickupWorkNonBlocking），
    // 成败都解除只读——打点是记录不是闸门，弱网时不能把司机锁在只读态里
    const handleStartWorkFromBanner = async () => {
        if (startingWork) {
            return;
        }
        const taskId = workStartInfo?.pendingTaskId;
        if (!taskId) {
            setWorkPreviewMode(false);
            return;
        }
        setStartingWork(true);
        try {
            // 时刻在点击这一刻定下来，不能等补传时再取——补传发生在恢复网络之后
            const clickedAt = new Date().toISOString();
            const recorded = await startPickupWorkNonBlocking(taskId, clickedAt);
            if (!recorded) {
                // 放行但不丢打点：落盘等下次进列表页有网时补传。
                // 只 toast 不落盘会让弱网下的打点静默丢失（司机在外跑时弱网是常态）
                await savePendingWorkStart(taskId, clickedAt);
                setPendingWorkStart(true);
                Toast.show({content: t('pages.pickUp.startWork.startFailed'), position: 'center'});
            }
            // 清 pendingTaskId 已收进 handleWorkStartDone（滑块路径也要），此处不再单独处理
            handleWorkStartDone();
        } finally {
            setStartingWork(false);
        }
    };

    // 两种拦截，dismissed 只管得着后一种：
    // - 有待开始任务 → 必拦。滑块是**任务维度**的（prd-clarify 1.2），司机一天多个任务各滑一次，
    //   调度临时加的任务同样要出滑块，不受「今天已经滑过一次」影响
    // - 今日无任务 → 滑块降级为「滑动查看历史作业」，滑过一次当天就别再拦
    // 今日任务都已开始（hasTodayTask 且 pendingTaskId 为空）则直接进列表。
    // workPreviewMode 是第三种出路：司机主动选了「只看不开工」，此时必须放行到列表（只读）
    // pendingWorkStart 这一项是断网场景的关键：司机点过开始作业但没传上去时，
    // pendingTaskId 仍是旧值，只靠它判断会让司机每次回到列表都被滑块拦一遍（dismissed 只管无任务那支）
    // startWorkEnabled 放在最前：未命中灰度的 hub 完全不渲染启动页，行为与本次改造前一致。
    // 后端 startPickupWork 另有一道按任务 hub 的拦截，前端这层只管显隐
    const needStartPage = !!workStartInfo?.startWorkEnabled && !workPreviewMode && !pendingWorkStart && (
        !!workStartInfo.pendingTaskId
        || (!workStartInfo.hasTodayTask && !workStartDismissed)
    );

    if (checkingWorkStart) {
        return (
            <div className="flex flex-col h-screen bg-gray-50 p-4 gap-2">
                <Skeleton.Title animated/>
                <Skeleton.Paragraph lineCount={3} animated/>
            </div>
        );
    }

    // dismissed 已并入 needStartPage，这里不能再叠一次——叠了会把「有待开始任务」也一起挡掉
    if (needStartPage) {
        return (
            <PickupStartWorkPage
                info={workStartInfo!}
                onDone={handleWorkStartDone}
                onRefresh={refreshWorkStartInfo}
                onPreview={handlePreviewTasks}
                onViewHistory={handleViewHistory}
            />
        );
    }

    return (
        <PickUpLayout
            title={`pickingUp`}
            banner={workPreviewMode
                ? <PickUpWorkPreviewBanner onStartWork={handleStartWorkFromBanner} submitting={startingWork}/>
                : undefined}
        >
            <Body retry={retry} readOnly={workPreviewMode}/>
        </PickUpLayout>
    );
};

export default PickingUpPage;